/**
 * Observer accounts in the Collaborator Access dialog. 1 Oct 2026, Josh.
 *
 * "Observer" is not a project role: it is an ACCOUNT with no paid licence, and
 * editing is gated on the account (activeProjectEntitled), never on
 * collaborators.<uid>.type. Making an Observer an Administrator, the owner, or a
 * Structure editor therefore changed a label and granted nothing, and users read
 * those options as a way to give edit access. On a normal project they are now
 * unavailable for Observers. Show Pass (unlocked) and source (issrc) projects let
 * Observers edit, so nothing changes there.
 *
 * Option (a): an Observer who is ALREADY an Administrator is left as they are;
 * the dialog says the role has no effect while they have an Observer account.
 *
 * accessInfo comes from the getProjectAccessInfo callable:
 *   {observers: {uid: bool}, ownerPlanFamily: 'educational'|'institutional'|'other'}
 * When it is missing (lookup failed) nothing is restricted: the old behaviour.
 */

export function observerOptionsMatter(project) {
    return !!project && !project.unlocked && !project.issrc;
}

export function accessRestriction({ project, uid, type, accessInfo }) {
    const isObserver = !!(accessInfo && accessInfo.observers && accessInfo.observers[uid] === true);
    const restricted = isObserver && observerOptionsMatter(project);
    return {
        restricted,
        isObserver,
        inactiveAdmin: restricted && type === 'administrator'
    };
}

function firstName(name) {
    const trimmed = String(name || '').trim();
    return trimmed ? trimmed.split(/\s+/)[0] : '';
}

export function observerExplanation({ name, ownerPlanFamily, viewerIsOwner }) {
    const who = firstName(name) || 'This collaborator';
    let text = `${who} has an Observer account, so they can view this production but not edit it. ` +
        'To give them more access, they need their own Cuelist subscription';
    // A seat is only on offer when the owner holds an educational or institutional plan.
    if (ownerPlanFamily === 'educational' || ownerPlanFamily === 'institutional') {
        text += viewerIsOwner ? ' or a seat in your institution' : ' or a seat in the owner\'s institution';
    }
    return `${text}.`;
}

/**
 * What the Collaborator Access options show (4 Oct 2026, Josh). An Observer on a
 * normal production gets a read-only "observer" option, shown selected, and every
 * other option greyed, so no ticked option promises editing. Display only: the
 * stored type is untouched and saving sends it back unchanged. Everyone else
 * (paid, Show Pass, unknown) gets exactly the options they had before.
 */
export function accessOptionState({ restricted, isOwner, isAdministrator, type }) {
    return {
        showObserver: restricted === true,
        selected: restricted ? 'observer' : type,
        disabled: {
            transfer: !isOwner || restricted === true,
            administrator: !isAdministrator || restricted === true,
            member: !isAdministrator || restricted === true,
            structure: !isAdministrator || restricted === true
        }
    };
}

export const INACTIVE_ADMIN_NOTE =
    'Their Administrator role has no effect while they have an Observer account.';

/**
 * The same rule, enforced where the write happens, so a stale screen cannot
 * send it. Throws an Error naming the reason; returns nothing when allowed.
 * currentType / currentCanEditStructure: what is stored now, so an existing
 * Observer-Administrator or Observer-with-Structure can be saved unchanged
 * (option a: nobody's existing access is taken away by this).
 */
export function checkAccessChange({ project, uid, accessInfo, type, canEditStructure, currentType, currentCanEditStructure }) {
    const { restricted } = accessRestriction({ project, uid, type, accessInfo });
    if (!restricted) return;
    if (type === 'transfer') {
        throw new Error('An Observer account cannot become the project owner.');
    }
    if (type === 'administrator' && currentType !== 'administrator') {
        throw new Error('An Observer account cannot be made an Administrator.');
    }
    if (type === 'member' && canEditStructure === true && currentCanEditStructure !== true) {
        throw new Error('An Observer account cannot be given Structure editing.');
    }
}



// WEBPACK FOOTER //
// ./src/lib/observer-access.js